import api from '@/api';

/**
 * Fachada de la sección pública de Impresión 3D.
 *
 * Fase 1 (entregada 06/05/2026): leía mocks estáticos.
 * Fase 2 (esta versión): consume el backend real bajo /api/v1/print3d/*.
 *
 * El SHAPE devuelto se mantiene IDÉNTICO al de la Fase 1 para no requerir
 * cambios en CarouselSection.vue, ProductCard.vue, QuoteForm.vue, etc.
 * Si los mocks (src/mocks/print3d.js) son útiles para QA offline, se conservan
 * pero ya no se importan aquí.
 */

// Convierte una URL de imagen guardada en BD a una URL absoluta usable por <img>:
//  - URLs absolutas (http, https, data:) → tal cual.
//  - Rutas relativas servidas por el backend (/uploads/...) → se prefijan con
//    el origen del backend (VUE_APP_API_URL sin el sufijo /api/v1).
//  - Cualquier otra cosa (fallback) → se devuelve sin cambios.
export function resolveImageUrl(url) {
    if (!url) return '';
    if (/^(https?:|data:)/i.test(url)) return url;
    if (url.startsWith('/uploads/')) {
        const apiUrl = process.env.VUE_APP_API_URL || '';
        const origin = apiUrl.replace(/\/api\/v1\/?$/, '');
        return origin + url;
    }
    return url;
}

/**
 * Catálogo público.
 *
 * Sin argumentos devuelve todos los productos activos (comportamiento previo
 * al catálogo general). Opciones:
 *   · `featured: true` → sólo los productos de temporada (carrusel principal).
 *   · `category` (slug) y `search` → filtros del catálogo general.
 */
export async function getProducts({ featured = null, category = '', search = '' } = {}) {
    try {
        const params = {};
        if (featured !== null) params.featured = String(featured);
        if (category) params.category = category;
        if (search) params.q = search;
        const res = await api.get('/print3d/products', { params });
        const products = (res.data.products || []).map((p) => {
            // Galería del producto. Los productos anteriores a la galería sólo
            // traen `imageUrl`: se normaliza a una lista de un elemento para
            // que el carrusel de la ficha no tenga que distinguir los casos.
            const source = Array.isArray(p.images) && p.images.length
                ? p.images
                : [p.imageUrl];
            const images = source.filter(Boolean).map(resolveImageUrl);
            return { ...p, images, imageUrl: images[0] || '' };
        });
        return { products, error: null };
    } catch (err) {
        console.error('[print3dService] getProducts', err);
        return { products: [], error: err.response?.data?.error || err.message };
    }
}

export async function getMaterials() {
    try {
        const res = await api.get('/print3d/materials');
        return { materials: res.data.materials, error: null };
    } catch (err) {
        console.error('[print3dService] getMaterials', err);
        return { materials: [], error: err.response?.data?.error || err.message };
    }
}

// Categorías del catálogo general (familias comerciales) con el número de
// productos activos de cada una. Distintas de `getPrintCategories`.
export async function getProductCategories() {
    try {
        const res = await api.get('/print3d/product-categories');
        return { categories: res.data.categories || [], error: null };
    } catch (err) {
        console.error('[print3dService] getProductCategories', err);
        return { categories: [], error: err.response?.data?.error || err.message };
    }
}

// Catálogo público de categorías de impresión ("Tipo de impresión") con sus
// umbrales de volumen para la autoselección del formulario.
export async function getPrintCategories() {
    try {
        const res = await api.get('/print3d/print-categories');
        return { categories: res.data.categories || [], error: null };
    } catch (err) {
        console.error('[print3dService] getPrintCategories', err);
        return { categories: [], error: err.response?.data?.error || err.message };
    }
}

function buildFormData(payload, requestType, file) {
    const fd = new FormData();
    fd.append('requestType', requestType);
    if (payload.contactName) fd.append('contactName', payload.contactName);
    if (payload.contactEmail) fd.append('contactEmail', payload.contactEmail);
    if (payload.contactPhone) fd.append('contactPhone', payload.contactPhone);
    if (payload.description) fd.append('description', payload.description);
    if (payload.dimensionsMm) {
        fd.append('dimensionsMm',
            typeof payload.dimensionsMm === 'string'
                ? payload.dimensionsMm
                : JSON.stringify(payload.dimensionsMm));
    }
    if (payload.volumeMm3 != null) fd.append('volumeMm3', String(payload.volumeMm3));
    if (payload.materialId != null) fd.append('materialId', String(payload.materialId));
    if (payload.categoryId != null) fd.append('categoryId', String(payload.categoryId));
    if (payload.quantity != null) fd.append('quantity', String(payload.quantity));
    if (file) fd.append('attachment', file);
    return fd;
}

export async function submitQuoteRequest(payload) {
    try {
        // Las "observaciones" del cliente viajan al backend bajo la clave
        // `description` (la columna se reutiliza para ambos request types).
        const apiPayload = {
            ...payload,
            description: payload.observations || payload.description || null,
        };
        const fd = buildFormData(apiPayload, 'print_only', payload.file);
        const res = await api.post('/print3d/quote-requests', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { receipt: res.data.receipt, error: null };
    } catch (err) {
        console.error('[print3dService] submitQuoteRequest', err);
        return { receipt: null, error: err.response?.data?.error || err.message };
    }
}

export async function submitModelingRequest(payload) {
    try {
        const fd = buildFormData(payload, 'modeling_and_print', payload.file);
        const res = await api.post('/print3d/quote-requests', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { receipt: res.data.receipt, error: null };
    } catch (err) {
        console.error('[print3dService] submitModelingRequest', err);
        return { receipt: null, error: err.response?.data?.error || err.message };
    }
}

export async function getQuotePreview({ materialId, volumeMm3, quantity, categoryId }) {
    try {
        const res = await api.post('/print3d/quote-preview', {
            materialId,
            volumeMm3,
            quantity,
            categoryId,
        });
        return { preview: res.data, error: null };
    } catch (err) {
        console.error('[print3dService] getQuotePreview', err);
        return { preview: null, error: err.response?.data?.error || err.message };
    }
}

/**
 * Pedido personalizado con imagen del usuario (rompecabezas, estuches…).
 *
 * Viaja por el mismo endpoint que el resto de solicitudes, como cuarto
 * `requestType`: así hereda el manejo de errores de subida, las validaciones
 * y la bandeja de órdenes del panel.
 *
 * Archivos que envía (plan 26_09_2026, §4.4):
 *   · `attachment` — la foto original del cliente.
 *   · `preview`    — el render de lo que aprobó en pantalla.
 *   · `die`        — STL del troquel, sólo con el estudio V2 activo.
 *   · `svg`        — imagen vectorizada, sólo con el estudio V2 activo.
 *
 * Los parámetros del estudio (`studio`) viajan como campos sueltos y no como
 * un JSON opaco: el servidor los valida uno a uno contra su propio catálogo.
 */
export async function submitCustomOrder(payload) {
    try {
        const fd = new FormData();
        fd.append('requestType', 'custom_order');
        fd.append('productType', payload.productType);
        fd.append('sizeId', payload.sizeId);
        if (payload.pieces != null) fd.append('pieces', String(payload.pieces));
        if (payload.caseOption) fd.append('caseOption', payload.caseOption);
        if (payload.crop) fd.append('crop', JSON.stringify(payload.crop));
        if (payload.personalizationText) fd.append('personalizationText', payload.personalizationText);

        const studio = payload.studio;
        if (studio) {
            fd.append('threshold', String(studio.threshold));
            fd.append('simplifyMm', String(studio.simplifyMm));
            fd.append('invert', String(studio.invert));
            fd.append('seed', studio.seed);
            fd.append('orientation', studio.orientation);
            fd.append('renderDpi', String(studio.renderDpi));
            fd.append('grid', JSON.stringify(studio.grid));
        }
        if (payload.artifactMeta) fd.append('artifactMeta', JSON.stringify(payload.artifactMeta));

        fd.append('contactName', payload.contactName);
        fd.append('contactEmail', payload.contactEmail);
        if (payload.contactPhone) fd.append('contactPhone', payload.contactPhone);
        if (payload.notes) fd.append('notes', payload.notes);
        if (payload.image) fd.append('attachment', payload.image);
        if (payload.preview) fd.append('preview', payload.preview, 'preview.jpg');
        if (payload.die) fd.append('die', payload.die.blob, payload.die.filename);
        if (payload.svg) fd.append('svg', payload.svg.blob, payload.svg.filename);

        const res = await api.post('/print3d/quote-requests', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { receipt: res.data.receipt, status: 'done', error: null };
    } catch (err) {
        console.error('[print3dService] submitCustomOrder', err);
        return {
            receipt: null,
            status: 'error',
            error: err.response?.data?.error || err.message,
        };
    }
}

export async function submitCatalogOrder(payload) {
    try {
        const res = await api.post('/print3d/quote-requests', {
            requestType: 'catalog_order',
            productId: payload.productId,
            quantity: payload.quantity,
            contactName: payload.contactName,
            contactEmail: payload.contactEmail,
            contactPhone: payload.contactPhone || null,
        });
        return { receipt: res.data.receipt, error: null };
    } catch (err) {
        console.error('[print3dService] submitCatalogOrder', err);
        return { receipt: null, error: err.response?.data?.error || err.message };
    }
}
